import { useState, useMemo } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { ChevronLeft, ChevronRight, Copy, X, ClipboardPaste, Dumbbell, Save, CalendarPlus } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const DOW = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const DAYNAMES = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function ProgramCalendar({ patientId, exercises, program, braceLogs = [], diaryLogs = [], onSaved }) {
  const [cursor, setCursor] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [schedule, setSchedule] = useState(() => {
    const ds = { ...(program?.date_schedule || {}) };
    Object.keys(ds).forEach((k) => { ds[k] = (ds[k] || []).filter((x) => !x.presence); });
    return ds;
  });
  const [presenceDates, setPresenceDates] = useState(() => {
    try { const n = JSON.parse(program?.notes || "{}"); return new Set(n.presence_dates || []); }
    catch { return new Set(); }
  });
  const [clipboard, setClipboard] = useState(null);
  const [saving, setSaving] = useState(false);

  const exMap = useMemo(() => Object.fromEntries((exercises || []).map((e) => [e.id, e])), [exercises]);
  const braceMap = useMemo(() => Object.fromEntries((braceLogs || []).map((l) => [l.date, l.hours])), [braceLogs]);
  const diaryMap = useMemo(() => {
    const m = {};
    (diaryLogs || []).forEach((e) => { const k = (e.created_at || e.date || "").slice(0, 10); if (k && !m[k]) m[k] = e; });
    return m;
  }, [diaryLogs]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday-based
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const exerciseDays = Object.keys(schedule).filter((k) => (schedule[k] || []).length > 0);
  const activeDays = new Set([...exerciseDays, ...presenceDates]);
  const totalEs = Object.values(schedule).reduce((s, arr) => s + (arr?.length || 0), 0);
  const totalMinutes = Object.values(schedule).reduce(
    (sum, arr) => sum + (arr || []).reduce((s, x) => s + (x.duration_minutes || 0), 0),
    0
  );

  const dayList = selectedDate ? schedule[selectedDate] || [] : [];
  const selectedIds = new Set(dayList.map((x) => x.exercise_id));
  const isPresence = selectedDate ? presenceDates.has(selectedDate) : false;

  const togglePresence = () => {
    setPresenceDates((prev) => {
      const n = new Set(prev);
      if (n.has(selectedDate)) n.delete(selectedDate); else n.add(selectedDate);
      return n;
    });
  };

  const toggleExercise = (ex) => {
    setSchedule((prev) => {
      const cur = prev[selectedDate] || [];
      const exists = cur.some((x) => x.exercise_id === ex.id);
      const next = exists
        ? cur.filter((x) => x.exercise_id !== ex.id)
        : [...cur, { exercise_id: ex.id, sets: ex.sets || 3, reps: ex.reps || 10, duration_seconds: ex.duration_seconds || 300, duration_minutes: ex.duration_seconds ? Math.max(1, Math.round(ex.duration_seconds / 60)) : 5 }];
      return { ...prev, [selectedDate]: next };
    });
  };

  const dayItem = (exId) => (schedule[selectedDate] || []).find((x) => x.exercise_id === exId);
  const setExerciseMinutes = (exId, val) => {
    const m = Math.max(0, parseInt(val, 10) || 0);
    setSchedule((prev) => ({
      ...prev,
      [selectedDate]: (prev[selectedDate] || []).map((x) => (x.exercise_id === exId ? { ...x, duration_minutes: m } : x)),
    }));
  };

  const clearDay = () => setSchedule((prev) => ({ ...prev, [selectedDate]: [] }));
  const copyDay = () => { setClipboard(schedule[selectedDate] || []); toast.success("Giorno copiato"); };
  const pasteDay = () => { if (clipboard) setSchedule((prev) => ({ ...prev, [selectedDate]: [...clipboard] })); };

  const save = async () => {
    const cleaned = Object.fromEntries(Object.entries(schedule).filter(([, v]) => (v || []).length > 0));
    const union = {};
    Object.values(cleaned).flat().forEach((x) => { union[x.exercise_id] = x; });
    setSaving(true);
    try {
      await api.createProgram({
        patient_id: patientId,
        exercises: Object.values(union),
        date_schedule: cleaned,
        notes: JSON.stringify({ presence_dates: [...presenceDates] }),
      });
      toast.success("Programma salvato");
      try {
        await api.sendMessage({
          receiver_id: patientId,
          content: "📋 Il tuo fisioterapista ha aggiornato il tuo programma di esercizi. Apri il calendario per vedere le novità.",
        });
      } catch { /* notifica non bloccante */ }
      onSaved && onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  // group exercises by category for the day editor
  const grouped = useMemo(() => {
    const g = {};
    (exercises || []).forEach((e) => { const c = e.category || "altro"; (g[c] = g[c] || []).push(e); });
    return g;
  }, [exercises]);

  return (
    <div>
      <Card className="border-border/70">
        <CardContent className="p-5">
          {/* Month nav */}
          <div className="mb-4 flex items-center justify-between">
            <Button variant="outline" size="icon" className="rounded-full" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <p className="text-lg font-bold text-[#1f3a5f]">{MONTHS[month]} {year}</p>
            <Button variant="outline" size="icon" className="rounded-full" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>

          {/* DOW header */}
          <div className="mb-1 grid grid-cols-7 gap-1 text-center text-xs font-medium text-muted-foreground">
            {DOW.map((d) => <div key={d}>{d}</div>)}
          </div>

          {/* Cells */}
          <div className="grid grid-cols-7 gap-1" data-testid="program-calendar">
            {cells.map((d, i) => {
              if (!d) return <div key={i} />;
              const key = iso(d);
              const arr = schedule[key] || [];
              const hasEx = arr.length > 0;
              const hasPresence = presenceDates.has(key);
              const hours = braceMap[key];
              const isSel = selectedDate === key;
              return (
                <button
                  key={i}
                  onClick={() => setSelectedDate(key)}
                  className={`flex min-h-[58px] flex-col items-center rounded-xl border p-1 text-sm transition-all ${isSel ? "border-primary bg-primary text-primary-foreground" : "border-transparent hover:bg-muted"}`}
                  data-testid={`cal-day-${d.getDate()}`}
                >
                  <span className="font-semibold">{d.getDate()}</span>
                  <span className="mt-0.5 flex gap-0.5">
                    {hasEx && <span className={`h-1.5 w-1.5 rounded-full ${isSel ? "bg-white" : "bg-[#5b87c9]"}`} />}
                    {hasPresence && <span className={`h-1.5 w-1.5 rounded-full ${isSel ? "bg-white" : "bg-[#5f9c96]"}`} />}
                    {diaryMap[key] && <span className={`h-1.5 w-1.5 rounded-full ${isSel ? "bg-white" : "bg-[#d9a441]"}`} />}
                  </span>
                  {hours != null && <span className={`mt-auto text-[10px] ${isSel ? "text-white/80" : "text-muted-foreground"}`}>{hours}h</span>}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#5b87c9]" /> Esercizi</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#5f9c96]" /> Seduta in presenza</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#d9a441]" /> Diario</span>
            </div>
            <span>{activeDays.size} giorni · {totalEs} es.</span>
          </div>
        </CardContent>
      </Card>

      {/* Day editor */}
      {selectedDate && (
        <Card className="mt-4 border-border/70" data-testid="day-editor">
          <CardContent className="p-5">
            {(() => { const dd = new Date(selectedDate); return (
              <p className="text-xl font-bold text-[#1f3a5f]">{DAYNAMES[dd.getDay()]} {dd.getDate()} {MONTHS[dd.getMonth()]}</p>
            ); })()}

            {diaryMap[selectedDate] && (() => { const dv = diaryMap[selectedDate]; const high = dv.pain_nrs > 7 || dv.difficulty > 7; return (
              <div className={`mt-3 rounded-xl border p-3 text-sm ${high ? "border-amber-300 bg-amber-50" : "border-[#f0d9a8] bg-[#fdf7e8]"}`} data-testid="day-diary">
                <p className="font-semibold text-[#1f3a5f]">Diario del paziente</p>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">Dolore {dv.pain_nrs}/10</span>
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Sforzo {dv.difficulty}/10</span>
                  {high && <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-800">⚠ Valore elevato (&gt;7)</span>}
                </div>
                {dv.sensation && <p className="mt-1.5 text-[#1f3a5f]"><span className="font-medium">Sensazione:</span> {dv.sensation}</p>}
                {dv.notes && <p className="mt-0.5 whitespace-pre-line text-foreground/80">{dv.notes}</p>}
              </div>
            ); })()}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" className="rounded-full" onClick={copyDay} data-testid="copy-day"><Copy className="mr-1.5 h-3.5 w-3.5" /> Copia giorno</Button>
              {clipboard && <Button variant="outline" size="sm" className="rounded-full" onClick={pasteDay} data-testid="paste-day"><ClipboardPaste className="mr-1.5 h-3.5 w-3.5" /> Incolla</Button>}
              <Button variant="outline" size="sm" className={`rounded-full ${isPresence ? "border-[#5f9c96] bg-[#e6f0e9] text-[#3f7a70]" : "text-[#3f7a70]"}`} onClick={togglePresence} data-testid="presence-day"><CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> {isPresence ? "In presenza ✓" : "Seduta in presenza"}</Button>
              <Button variant="outline" size="sm" className="rounded-full text-destructive" onClick={clearDay} data-testid="clear-day"><X className="mr-1.5 h-3.5 w-3.5" /> Svuota</Button>
            </div>

            <div className="mt-4 space-y-4">
              {Object.entries(grouped).map(([category, list]) => {
                const selCount = list.filter((e) => selectedIds.has(e.id)).length;
                return (
                  <div key={category}>
                    <p className="mb-2 text-sm font-bold capitalize text-[#1f3a5f]">{category} <span className="font-normal text-muted-foreground">· {list.length} eserciz{list.length === 1 ? "io" : "i"} · {selCount} selezionat{selCount === 1 ? "o" : "i"}</span></p>
                    <div className="space-y-2">
                      {list.map((e) => {
                        const on = selectedIds.has(e.id);
                        return (
                          <div key={e.id}
                            className={`flex w-full items-center gap-3 rounded-xl border p-3 transition-all ${on ? "border-primary bg-primary/5" : "border-border bg-white hover:border-primary/40"}`}>
                            <HoverCard openDelay={150} closeDelay={80}>
                              <HoverCardTrigger asChild>
                                <div
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => toggleExercise(e)}
                                  onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); toggleExercise(e); } }}
                                  data-testid={`day-ex-${e.id}`}
                                  className="flex flex-1 cursor-pointer items-center gap-3 text-left"
                                >
                                  <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${on ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}><Dumbbell className="h-4 w-4" /></div>
                                  <span className="flex-1 text-sm font-medium text-[#1f3a5f] underline-offset-2 hover:underline" data-testid={`day-ex-name-${e.id}`}>{e.title}</span>
                                </div>
                              </HoverCardTrigger>
                              <HoverCardContent className="w-80" data-testid={`day-ex-desc-${e.id}`}>
                                <p className="font-bold text-[#1f3a5f]">{e.title}</p>
                                {e.objective && <p className="mt-1 text-xs font-medium text-primary">{e.objective}</p>}
                                <p className="mt-1.5 text-sm text-muted-foreground">{e.description || "Nessuna descrizione disponibile."}</p>
                                <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                                  {e.level && <span className="rounded-full bg-[#eaf1f6] px-2 py-0.5 capitalize text-primary">{e.level}</span>}
                                  {e.district && <span className="rounded-full bg-muted px-2 py-0.5 capitalize">{String(e.district).replace(/_/g, " ")}</span>}
                                  {e.duration_seconds ? <span className="rounded-full bg-muted px-2 py-0.5">{Math.round(e.duration_seconds / 60)} min</span> : null}
                                  {e.sets && e.reps ? <span className="rounded-full bg-muted px-2 py-0.5">{e.sets}x{e.reps}</span> : null}
                                </div>
                              </HoverCardContent>
                            </HoverCard>
                            {on && (
                              <div className="flex shrink-0 items-center gap-1.5">
                                <Input type="number" min="0" value={dayItem(e.id)?.duration_minutes ?? ""} onChange={(ev) => setExerciseMinutes(e.id, ev.target.value)} className="h-8 w-16" data-testid={`day-ex-min-${e.id}`} />
                                <span className="text-xs text-muted-foreground">min</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="mt-4 flex items-center justify-between gap-4 rounded-xl bg-[#eef4fb] p-4" data-testid="program-total-minutes">
        <div>
          <p className="text-sm text-muted-foreground">Minuti assistenziali totali (stima)</p>
          <p className="text-3xl font-bold text-[#1f3a5f]"><span data-testid="program-total-minutes-value">{totalMinutes}</span> min</p>
        </div>
        <p className="max-w-[220px] text-right text-xs text-muted-foreground">Aggiungi o rimuovi esercizi se il carico è troppo alto o troppo basso.</p>
      </div>

      <Button className="mt-4 h-12 w-full text-base" onClick={save} disabled={saving} data-testid="save-program-button">
        <Save className="mr-2 h-5 w-5" /> {saving ? "Salvataggio..." : "Salva programma"}
      </Button>
    </div>
  );
}

